import * as Linking from "expo-linking";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";
import {
  Text,
  View,
  TouchableOpacity,
  ScrollView,
  Platform,
  Image,
  Pressable,
  DeviceEventEmitter,
} from "react-native";
import AppContainer from "@/components/shared/AppContainer";
import React, { useCallback, useEffect, useState } from "react";
import AppActivityIndicator from "@/components/shared/AppActivityIndicator";
import tw from "twrnc";
import useWorkspaceAuth, {
  useEventAuth,
} from "@/components/teams/useWorkspaceAuth";
import { useTeamAccess } from "@/components/teams/useWorkspace";
import { teamsErrorMessage } from "@/components/teams/errors";
import WorkspaceBanner from "@/components/teams/WorkspaceBanner";
import { notify } from "@/components/shared/AppToast";
import Callout from "@/components/settings/Callout";
import {
  formatEventDate,
  makeUrl,
  makeDownloadUrl,
  roomNounText,
  eventTitle,
} from "@/components/globalUtils";
import { openStreamWindow } from "@/components/Utils";
import { hasDesktopStudio } from "@/components/desktop/desktopApp";
import analytics from "@/analytics";
import useSubscribeToEventUpdates from "@/components/hooks/useSubscribeToEventUpdates";
import { unlockWithCredit } from "@/components/http/events";
import RoomSwitcher from "@/components/rooms/RoomSwitcher";
import { isGrouped, isRoom } from "@/components/rooms/rooms";
import { rememberRoomCopy, roomCopyOf } from "@/components/rooms/roomCopies";
import { isCameraGroup, isExtraCamera } from "@/components/rooms/multicam";
import { useFrame } from "@/components/layout/FrameContext";
import useEventMenu from "./useEventMenu";
import AppHeader, { onBackPressed } from "@/components/shared/AppHeader";
import { Ionicons } from "@expo/vector-icons";
import OnboardingButton from "@/components/shared/OnboardingButton";
import { LiveIndicator, TestIndicator } from "@/components/shared/Buttons";
import GoLiveButton from "@/components/shared/GoLiveButton";
import useSharePress from "@/components/hooks/useSharePress";
import NewTestButton from "@/components/shared/NewTestButton";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import { useSafeAreaFrame } from "react-native-safe-area-context";
import ChangeCoverImageButton from "@/components/shared/ChangeCoverImageButton";
import HelpTip from "@/components/settings/HelpTip";
import {
  formatEventExpiryDate,
  getEventExpiryMoment,
  getExpiryUrgency,
  shouldShowStorageExpiry,
} from "@/components/shared/eventStorageUtils";
import { t, tn } from "@/localization";

let StoreReview;
if (Platform.OS !== "web") {
  StoreReview = require("react-native-store-review");
}

const EventEdit = ({ route, navigation }) => {
  const { slug, event: initialEvent } = route.params || {};

  const isMobileUX = useIsMobileUX();
  const safeAreaFrame = useSafeAreaFrame();
  // On wide layouts this screen is the event's overview, next to the event menu
  // (components/layout/EventWorkspace.js), which has the close button, title and menu.
  const frame = useFrame();
  const inPane = frame?.kind === "pane";

  const [unlockbuttonloading, setunlockbuttonloading] = useState(false);
  // Overview pane: heights of the cover block and of the actions, to keep the
  // actions at the bottom only while everything fits.
  const [paneHeights, setPaneHeights] = useState({ content: 0, actions: 0 });
  const measurePane = (key) => (e) => {
    const height = Math.round(e.nativeEvent.layout.height);
    setPaneHeights((h) => (h[key] === height ? h : { ...h, [key]: height }));
  };

  // The account the event is in: yours, or the one you're working in (Teams).
  const { broadcaster } = useWorkspaceAuth();

  // The event as it came with the route, or, after switching rooms in place
  // (phones), that room's copy from the chips.
  const routeCopy =
    initialEvent?.slug === slug && initialEvent.title
      ? initialEvent
      : roomCopyOf(slug);
  const { event: loaded, error } = useSubscribeToEventUpdates(
    routeCopy || { broadcaster, slug }
  );
  // Never the previous room while the one switched to is loading.
  const event = loaded?.slug === slug || !routeCopy ? loaded : routeCopy;

  // TODO: Update this based on is_published & if unlock param changed in props
  // Teams: the plan, credits and custom domain of the event's account.
  const {
    credits,
    subscription_access_expiry_datetime,
    refetchProfile,
    custom_domain,
    account_type,
  } = useEventAuth(event);
  // Teams: what your role lets you do with this event (all of it in your
  // own account).
  const team = useTeamAccess();
  const allowed = (permission) => team.can(permission, event);
  const memberView = team.isShared && event?.broadcaster !== team.self;

  const [loadingGoLive, setLoadingGoLive] = useState(false);

  // Phones: switch rooms in place. The screen, its header and the chips stay
  // put and only the room's details change, as with the menu on wide layouts
  // (replacing the screen showed a spinner and slid a new screen in).
  const switchRoom = (member) => {
    rememberRoomCopy(member);
    navigation.setParams({ slug: member.slug, event: undefined });
  };

  const {
    menu,
    modals,
    now,
    isAfter,
    archived,
    groupPrimary,
    groupRooms,
    roomsAddable,
    roomsRenamable,
    roomNoun,
    onAddRoomPress,
  } = useEventMenu({
    event,
    slug,
    navigation,
    switchRoom: inPane ? frame.switchRoom : switchRoom,
  });

  // useFocusEffect
  // const [event, setEvent] = useState(initialEvent);

  useEffect(() => {
    if (error || !slug) {
      navigation.navigate("EventList");
    }
  }, [error, slug]);

  const {
    event_cover_path,
    title,
    alias,
    stream_key,
    is_published,
    is_test,
    event_status,
    days = 1,
    days_paid,
    event_datetime_utc,
    event_timezone,
    event_window_end_datetime_utc,
    event_window_start_datetime_utc,
  } = event;

  const daysToPay = is_published ? 0 : days - days_paid;

  const eventDate = formatEventDate(event_datetime_utc, event_timezone);
  // Unpublished multi-day events may have no end yet.
  const multiDayEnd = formatEventDate(
    event_window_end_datetime_utc,
    event_timezone
  );

  // A camera other than the main one: guests watch, sign the guestbook and
  // download on the main camera's link.
  const extraCamera = isExtraCamera(event);
  const linkEvent = extraCamera && groupPrimary ? groupPrimary : event;
  const url = makeUrl(linkEvent, custom_domain);
  const downloadUrl = makeDownloadUrl(linkEvent, custom_domain);

  const linkPress = useCallback(() => {
    analytics.track("Preview Link");
    if (Platform.OS === "web") {
      window.open(url, "_blank");
    } else Linking.openURL(url);
  }, [url]);

  const downloadPress = useCallback(() => {
    if (Platform.OS === "web") {
      window.open(downloadUrl, "_blank");
    } else {
      Linking.openURL(downloadUrl);
      StoreReview.requestReview();
    }
  }, [downloadUrl]);

  const openExternalUrl = useCallback((targetUrl) => {
    if (!targetUrl) return;
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(targetUrl, "_blank", "noopener,noreferrer");
      return;
    }
    Linking.openURL(targetUrl);
  }, []);

  const goLive = useCallback(async () => {
    if (Platform.OS === "web" && !hasDesktopStudio()) {
      navigation.navigate("HowToStream", { event, slug });
    } else {
      if (loadingGoLive) return;
      setLoadingGoLive(true);
      await openStreamWindow(event);
      // navigation.navigate("AskReview");
      setLoadingGoLive(false);
    }
  }, [
    slug,
    stream_key,
    title,
    event_datetime_utc,
    event_window_end_datetime_utc,
    event_window_start_datetime_utc,
    event_status,
  ]);

  const maxWidth = 640;
  const margin = 24;

  const imageWidth = Math.min(
    (inPane ? frame.width : safeAreaFrame.width) - margin * 2,
    maxWidth - margin * 2
  );
  // TODO: max-width

  const expiryMoment = getEventExpiryMoment(event);
  const expiryDate = formatEventExpiryDate(expiryMoment, event_timezone);
  const daysUntilExpiry = expiryMoment
    ? Math.ceil(expiryMoment.diff(now, "days", true))
    : null;
  const expiryUrgency = getExpiryUrgency(expiryMoment, now);
  // Storage purchases are the account holder's (Teams).
  const showStorageCtas = account_type !== "business" && allowed("buy");
  const showExtendLinkCta =
    showStorageCtas &&
    shouldShowStorageExpiry(event, now) &&
    !!expiryDate &&
    !archived &&
    Number.isFinite(daysUntilExpiry) &&
    daysUntilExpiry < 365;
  const expiringSoon =
    Number.isFinite(daysUntilExpiry) &&
    daysUntilExpiry > 0 &&
    daysUntilExpiry <= 30;
  const showUnarchiveCta = showStorageCtas && archived;
  const downloadAvailable =
    !archived &&
    is_published &&
    !is_test &&
    event_status === "past" &&
    allowed("video");
  const extendLinkUrl =
    event?.broadcaster && event?.slug
      ? `https://evt.live/api/upgrade/${event.broadcaster}/${event.slug}`
      : null;
  const unarchiveUrl = stream_key
    ? `https://evt.live/api/payRestore/${stream_key}`
    : null;

  const expiryTitle =
    expiryUrgency === "critical"
      ? t("Expiring soon")
      : expiryUrgency === "warning"
      ? t("Expiring soon")
      : t("Storage expiry");

  const expiryTitleStyle =
    expiryUrgency === "critical"
      ? tw`text-red-700`
      : expiryUrgency === "warning"
      ? tw`text-amber-800`
      : tw`text-gray-800`;

  const subscriptionActive =
    subscription_access_expiry_datetime > moment().utc().format();

  // Teams: an admin unlocks with the account's credits (or its Unlimited
  // plan). Members can't see the balance, so the server says if it's short.
  const unlockAsMember = async () => {
    if (unlockbuttonloading) return;
    setunlockbuttonloading(true);
    try {
      await unlockWithCredit(slug, daysToPay > 0 ? daysToPay : 1, {
        throwOnError: true,
      });
      DeviceEventEmitter.emit("refresh_event");
      await refetchProfile();
      notify.success(t("Event unlocked"));
    } catch (err) {
      notify.error(
        t("Couldn't unlock this event"),
        teamsErrorMessage(
          err,
          t(
            "{team}'s account may not have enough credits. Ask the account holder to add more.",
            { team: team.name }
          )
        )
      );
    } finally {
      setunlockbuttonloading(false);
    }
  };

  const { sharePress, ShareModal } = useSharePress(eventTitle(event), url);

  const handleCoverImageComplete = useCallback(() => {
    // Optionally refresh the event data or show success message
    console.log("Cover image uploaded successfully");
  }, []);

  const myTimezone = moment.tz.guess();

  const roomNote = isGrouped(event) && (
    <View style={tw`mt-1 mb-2`}>
      {!inPane && (
        <RoomSwitcher
          current={event}
          primary={groupPrimary || (isRoom(event) ? null : event)}
          rooms={groupRooms}
          onSelect={switchRoom}
          onAdd={roomsAddable ? onAddRoomPress : undefined}
          renamable={roomsRenamable}
        />
      )}
      <Text style={tw`text-xs text-gray-600 mt-1`}>
        {isCameraGroup(event)
          ? isRoom(event)
            ? t(
                "Guests use your main link and switch to this camera there. Stream it from its own phone."
              )
            : t(
                "Share this link: guests switch between the cameras while they watch. Each camera streams from its own phone."
              )
          : isRoom(event)
          ? t(
              "Guests use the main link and switch {noun}s on the event page. This {noun} streams with its own key.",
              { noun: roomNounText(roomNoun) }
            )
          : t(
              "Share this link: guests switch {noun}s on the event page. Each {noun} streams with its own key.",
              { noun: roomNounText(roomNoun) }
            )}
      </Text>
    </View>
  );

  // Teams: only roles that may edit the event change its cover.
  const cover =
    archived || !allowed("editEvent") ? (
      <Image
        source={{ uri: event_cover_path }}
        style={[
          tw`rounded-[5px] border border-gray-200`,
          { width: imageWidth, height: (imageWidth * 720) / 1280 },
        ]}
        blurRadius={archived ? 8 : 0}
      />
    ) : (
      <View>
        <ChangeCoverImageButton
          event={event}
          imageWidth={imageWidth}
          onComplete={handleCoverImageComplete}
          showHoverMessage={true}
        />
      </View>
    );

  const linkRow = !archived && (
    <View style={tw`flex-row items-center justify-between`}>
      <View style={tw`flex-1`}>
        <View style={tw`flex-row items-center mb-1`}>
          <Text style={tw`text-sm text-gray-500`}>{t("Your event link:")}</Text>
          <HelpTip topic="overview.eventLink" size={14} />
        </View>
        <Pressable
          onPress={linkPress}
          style={({ pressed }) => [
            tw`flex-row items-center`,
            { opacity: pressed ? 0.7 : 1 },
          ]}
        >
          <Text
            style={tw`text-[#F84123] underline font-bold`}
            numberOfLines={1}
          >
            {url.replace("https://", "")}
          </Text>
        </Pressable>
      </View>
      <Pressable
        style={({ pressed }) => [
          tw`flex-row items-center bg-[#FFF6F5] rounded-lg px-4 py-3 ml-3`,
          { opacity: pressed ? 0.8 : 1.0 },
        ]}
        onPress={sharePress}
      >
        <Ionicons
          name="arrow-redo-outline"
          size={20}
          style={tw`text-[#3F5B7A] mr-2`}
        />
        <Text style={tw`text-[#3F5B7A] font-medium`}>{t("Share")}</Text>
      </Pressable>
    </View>
  );

  // Business accounts: their label has always had a space before "Unlock"
  // (two after "3-day event:").
  const unlockWithCreditText =
    account_type === "business"
      ? days_paid > 0
        ? tn(
            "{days}-day event:  Unlock with {count} more credit",
            "{days}-day event:  Unlock with {count} more credits",
            daysToPay,
            { days }
          )
        : " " +
          tn(
            "Unlock with {count} credit",
            "Unlock with {count} credits",
            daysToPay
          )
      : days_paid > 0
      ? daysToPay > 1
        ? t("{days}-day event: Unlock with {count} prepaid events", {
            days,
            count: daysToPay,
          })
        : t("{days}-day event: Unlock with your prepaid event", { days })
      : daysToPay > 1
      ? t("Unlock with {count} prepaid events", { count: daysToPay })
      : t("Unlock with your prepaid event");

  const actions = (
    <View style={tw`max-w-[640px] w-full self-center flex my-2.5`}>
      {!archived && !isAfter && allowed("goLive") && (
        <GoLiveButton event={event} goLive={goLive} style={tw`my-2.5`} />
      )}

      {showExtendLinkCta && expiringSoon && (
        <View style={tw`mb-2.5 py-1`}>
          <Text style={[tw`text-sm font-extrabold`, expiryTitleStyle]}>
            {expiryTitle}
          </Text>
          <Text style={tw`mt-1 text-sm text-gray-700 leading-5`}>
            {t(
              "Your private event link is scheduled to expire on {date}. Keep your event page, the video and the guestbook online for 1 more year, or for 10 years.",
              { date: expiryDate }
            )}
          </Text>
          <OnboardingButton
            text={t("Keep My Memories Online Longer")}
            onPress={() => openExternalUrl(extendLinkUrl)}
            dark={!expiringSoon}
            bordered={!expiringSoon}
            style={tw`mt-3 mb-0`}
          />
        </View>
      )}

      {downloadAvailable && (
        <OnboardingButton
          text={t("Download & View Stats")}
          onPress={downloadPress}
          icon="download"
          dark={showExtendLinkCta && expiringSoon}
          bordered={showExtendLinkCta && expiringSoon}
          style={
            showExtendLinkCta && expiringSoon
              ? tw`mb-2.5`
              : !isAfter && tw`bg-[#3f5b7a]`
          }
        />
      )}

      {showExtendLinkCta && !expiringSoon && (
        <View style={tw`mb-2.5 py-1`}>
          <Text style={[tw`text-sm font-extrabold`, expiryTitleStyle]}>
            {expiryTitle}
          </Text>
          <Text style={tw`mt-1 text-sm text-gray-700 leading-5`}>
            {t(
              "Your private event link is scheduled to expire on {date}. Keep your event page, the video and the guestbook online for 1 more year, or for 10 years.",
              { date: expiryDate }
            )}
          </Text>
          <OnboardingButton
            text={t("Keep My Memories Online Longer")}
            onPress={() => openExternalUrl(extendLinkUrl)}
            dark
            bordered
            style={tw`mt-3 mb-0`}
          />
        </View>
      )}

      {showUnarchiveCta && (
        <View style={tw`mb-2.5 px-0 py-1`}>
          <Text style={tw`text-base font-extrabold text-[#3F5B7A]`}>
            {t("This Event is in the Secure Archive.")}
          </Text>
          <Text style={tw`mt-2 text-sm text-gray-700 leading-6`}>
            {t(
              "The public streaming period for this event has ended. The streaming link has expired, but the memories are safe in our archive."
            )}
          </Text>
          {!!unarchiveUrl && (
            <OnboardingButton
              text={t("Restore this Event")}
              onPress={() => openExternalUrl(unarchiveUrl)}
              style={tw`mt-4 mb-0`}
            />
          )}
        </View>
      )}

      {memberView && !archived && !is_published && (
        <View>
          {allowed("goLive") && (
            <NewTestButton
              style={tw`my-2.5 h-[62px]`}
              relatedEvent={event}
              forceNew={false}
              title={t("Start free test")}
              openStreamDirectly={true}
            />
          )}
          {allowed("spendCredits") ? (
            <OnboardingButton
              text={t("Unlock event")}
              style={tw`my-2.5`}
              loading={unlockbuttonloading}
              onPress={unlockAsMember}
            />
          ) : (
            allowed("goLive") && (
              <Callout tone="info" style={tw`my-2.5`}>
                {t(
                  "This event isn't unlocked yet. Ask an admin of {team}'s account to unlock it.",
                  { team: team.name }
                )}
              </Callout>
            )
          )}
        </View>
      )}

      {!memberView &&
        !archived &&
        !is_published &&
        (credits < daysToPay || !daysToPay) &&
        !subscriptionActive && (
          <View>
            <NewTestButton
              style={tw`my-2.5 h-[62px]`}
              relatedEvent={event}
              forceNew={false}
              title={t("Start free test")}
              openStreamDirectly={true}
            />
            <OnboardingButton
              text={extraCamera ? t("Unlock cameras") : t("Unlock event")}
              style={tw`my-2.5`}
              onPress={() => {
                // Cameras are bought together, on the main camera's paywall.
                const payFor =
                  extraCamera && groupPrimary ? groupPrimary : event;
                navigation.navigate("PayWall", {
                  event: payFor,
                  slug: payFor.slug,
                });
              }}
            />
          </View>
        )}

      {!memberView &&
        !archived &&
        !is_published &&
        ((credits >= daysToPay && daysToPay > 0) || subscriptionActive) && (
          <OnboardingButton
            style={tw`mb-0`}
            loading={unlockbuttonloading}
            onPress={async () => {
              setunlockbuttonloading(true);
              await unlockWithCredit(slug, daysToPay);
              await refetchProfile();
              setunlockbuttonloading(false);
            }}
            text={unlockWithCreditText}
          />
        )}
    </View>
  );

  // Next to the event menu: the cover and link centred, the actions at the
  // bottom like the guest list's buttons. When that doesn't fit (a short
  // window, a tall cover), the actions follow the link and scroll with it.
  if (inPane) {
    const pinActions =
      paneHeights.content + paneHeights.actions + 60 <= frame.height;
    const actionsBlock = (
      <View onLayout={measurePane("actions")}>{actions}</View>
    );
    return (
      <AppContainer>
        <ScrollView
          contentContainerStyle={tw`w-full max-w-[640px] self-center px-6 py-6 flex-grow justify-center`}
        >
          <ShareModal />
          {!title && <AppActivityIndicator />}
          {title && (
            <View onLayout={measurePane("content")}>
              {roomNote}
              {cover}
              <View style={tw`mt-5`}>{linkRow}</View>
            </View>
          )}
          {title && !pinActions && actionsBlock}
        </ScrollView>
        {title && pinActions && (
          <View style={tw`w-full max-w-[640px] self-center px-6 pb-3`}>
            {actionsBlock}
          </View>
        )}
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <AppHeader
        leftButtonComponent={
          <TouchableOpacity
            activeOpacity={1}
            onPress={() => onBackPressed(navigation, route)}
          >
            <Ionicons name="close-outline" size={30} color="#F84123" />
          </TouchableOpacity>
        }
        hasTitle={false}
        hasRightButton={false}
      />
      {/* Teams: the web shows this banner above every screen (App.js); in the
          apps, the event list and the event say whose account it is. */}
      {Platform.OS !== "web" && (
        <WorkspaceBanner navigation={navigation} style={tw`mb-2`} />
      )}

      <ScrollView
        contentContainerStyle={tw`w-full max-w-[640px] self-center px-6`}
      >
        <ShareModal />
        {!title && <AppActivityIndicator />}
        {title && (
          <>
            <Text
              style={[
                tw`font-bold text-left max-w-[${imageWidth}px] flex-shrink flex-wrap`,
                isMobileUX ? tw`text-[27px]` : tw`text-[34px]`,
              ]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {eventTitle(event)}{" "}
              {event_status === "live" &&
                (is_test ? (
                  <TestIndicator style={tw`mr-2.5`} />
                ) : (
                  <LiveIndicator style={tw`mr-2.5`} />
                ))}
            </Text>

            {!is_test && (
              <Text
                style={tw`text-sm font-extrabold text-left tracking-normal leading-normal my-1.5 mr-2.5`}
              >
                {days <= 1
                  ? eventDate
                  : multiDayEnd
                  ? t("{days}-Day Event: {start} – {end}", {
                      days,
                      start: eventDate,
                      end: multiDayEnd,
                    })
                  : t("{days}-Day Event: {start}", {
                      days,
                      start: eventDate,
                    })}

                {myTimezone !== event.event_timezone &&
                  ` (${event.event_timezone})`}
              </Text>
            )}

            {roomNote}

            {cover}

            <View style={tw`mt-5`}>
              {linkRow}
              {actions}
            </View>

            <View style={tw`mb-10`}>
              {menu.map((item) => (
                <TouchableOpacity
                  key={item.title}
                  onPress={
                    item.disabled
                      ? undefined
                      : () =>
                          item.onPress
                            ? item.onPress()
                            : navigation.navigate(item.screen || "EventEdits", {
                                event,
                                slug,
                              })
                  }
                  style={tw`w-full py-4 border-b border-gray-200 active:bg-gray-50 flex-row items-center`}
                >
                  <Ionicons
                    name={item.icon}
                    size={22}
                    color={item.destructive ? "#F84123" : "#3F5B7A"}
                    style={tw`mr-3`}
                  />
                  <Text
                    style={[
                      tw`text-base flex-1`,
                      item.destructive && tw`text-[#F84123]`,
                    ]}
                  >
                    {item.title}
                  </Text>
                  {!item.hideChevron && (
                    <Ionicons
                      name="chevron-forward"
                      size={20}
                      color="#AEAEB2"
                    />
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </>
        )}
      </ScrollView>
      {modals}
    </AppContainer>
  );
};

export default EventEdit;
